/**
 * GeoTreeRail — the Discover Communities catalog's geography drilldown
 * (DEV-1447 slice 2).
 *
 * Country → state → city. Selecting a node scopes the whole catalog (shelves
 * AND grid) to that subtree.
 *
 * ── WHY THIS IS ARCHITECTURALLY LEGAL ─────────────────────────────────────
 * POLICY_NAVIGATION_RAILS §3 allows LeftNav + exactly ONE Rail. On the catalog
 * route the Activity drawer is gone (the catalog replaced the globe AND the
 * drawer — §7.1), so this is Discover's one Rail, not a third column. It is
 * also not pillar navigation — that lives on the PillarBar, on the hub. This is
 * a "secondary context list for the current section," which is the policy's own
 * definition of a Rail.
 *
 * Adopts the shared <Rail> primitive: it owns width, opaque background,
 * divider, collapse + persistence, RTL and the responsive breakpoint. Only the
 * rows are ours. (Do not re-fork the chrome — §4 of the policy.)
 *
 * ── SELECTION LIVES IN THE URL ────────────────────────────────────────────
 * The rail and the catalog page are SIBLINGS in the shell (App.tsx mounts rails
 * in `l4e-content-row`, beside the routed page), not parent and child. So the
 * selected geo is a query param — `?geo=<guid>` — which both read. That also
 * composes with §7.1's routes-not-state ruling and makes a drilled-in view
 * deep-linkable and Back-navigable, for free.
 *
 * ── THE INACTIVE-GEO CARVE-OUT ────────────────────────────────────────────
 * Inactive geos render here as non-followable PATH SEGMENTS (dimmed, no follow
 * affordance) but remain expandable and selectable as a scope. Omission from
 * the catalog's answer set (§7.5) must never become omission from the
 * navigation path — otherwise a city under an inactive state is unreachable.
 * See geoTree.ts.
 */

import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { usePersistentCollapse } from '../../hooks/usePersistentCollapse';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import Rail, { RAIL_NARROW_MAX } from './Rail';
import {
  buildEntityMap,
  buildParentMap,
  type CommunityEntity,
  type CommunityRelationship,
} from '../CommunityCard/communityCardData';
import { isCatalogEligible } from '../../Pages/DiscoverCatalog/catalogScope';
import { buildGeoTree, pathToNode, type GeoNode } from '../../Pages/DiscoverCatalog/geoTree';
import styles from './GeoTreeRail.module.css';

interface Props {
  entities?: CommunityEntity[];
  relationships?: CommunityRelationship[];
}

/** Depth beyond which children start collapsed. Countries expand; cities don't. */
const AUTO_EXPAND_DEPTH = 1;

export default function GeoTreeRail({ entities, relationships }: Props) {
  const { t } = useAppTranslation('discover');
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const selected = (params.get('geo') || '').toUpperCase();

  /**
   * CONTROLLED collapse (Skip, 2026-07-13).
   *
   * The Rail primitive can own collapse itself (uncontrolled, via `persistKey`),
   * but then the adopter never learns the flag — and a tree crushed into the
   * 56px collapsed strip renders as a column of orphaned chevrons and counts.
   * Rail's own docs sanction taking control precisely "when the adopter also
   * needs the flag to restyle its rows".
   *
   * Collapsed, this rail shows ONE thing: the word "Geographies", rotated. No
   * icons, no truncated tree — there is no honest 56px representation of a
   * hierarchy, and a messy one is worse than none.
   *
   * `useIsNarrow` is re-checked here because Rail FORCE-collapses below the
   * breakpoint regardless of the prop; without this the adopter would render
   * the full tree into a strip that Rail has already collapsed.
   */
  const persisted = usePersistentCollapse('l4e_rail_discover_geo_collapsed', false);
  const isNarrow = useIsNarrow(RAIL_NARROW_MAX);
  const collapsed = isNarrow || persisted.collapsed;

  // Hooks must run unconditionally (mirrors CommunitySwitcherRail) — the
  // section gate is applied to the RENDER, below, never to the hook order.
  const parentMap = useMemo(() => buildParentMap(relationships), [relationships]);

  const countable = useMemo(
    () =>
      new Set(
        (entities || [])
          .filter(isCatalogEligible)
          .map((e) => e.entity_guid.toUpperCase()),
      ),
    [entities],
  );

  const roots = useMemo(
    () => buildGeoTree(entities, parentMap, countable),
    [entities, parentMap, countable],
  );

  /** Ancestors of the selection are force-expanded so a deep link opens with
   *  its path already unfolded. */
  const selectedPath = useMemo(
    () => new Set(pathToNode(roots, selected).map((n) => n.guid)),
    [roots, selected],
  );

  const [manuallyToggled, setManuallyToggled] = useState<Record<string, boolean>>({});

  const isExpanded = (node: GeoNode, depth: number) => {
    const manual = manuallyToggled[node.guid];
    if (manual !== undefined) return manual;
    if (selectedPath.has(node.guid)) return true;
    return depth < AUTO_EXPAND_DEPTH;
  };

  const toggle = (guid: string, next: boolean) =>
    setManuallyToggled((m) => ({ ...m, [guid]: next }));

  const select = (guid: string | null) => {
    const next = new URLSearchParams(params);
    if (guid) next.set('geo', guid);
    else next.delete('geo');
    navigate({ pathname: location.pathname, search: next.toString() });
  };

  // Section gate — this Rail belongs to the Discover Communities catalog only.
  if (!location.pathname.startsWith('/discover/communities')) return null;

  const renderNode = (node: GeoNode, depth: number): React.ReactNode => {
    const expanded = isExpanded(node, depth);
    const hasChildren = node.children.length > 0;
    const isSelected = node.guid === selected;

    return (
      <li key={node.guid} className={styles.item}>
        <div
          className={[
            styles.row,
            isSelected ? styles.rowSelected : '',
            node.inactive ? styles.rowInactive : '',
          ].filter(Boolean).join(' ')}
          style={{ paddingInlineStart: 6 + depth * 12 }}
        >
          {hasChildren ? (
            <button
              type="button"
              className={styles.twisty}
              onClick={() => toggle(node.guid, !expanded)}
              aria-expanded={expanded}
              aria-label={
                expanded
                  ? t('catalog.tree.collapseNode', { name: node.name })
                  : t('catalog.tree.expandNode', { name: node.name })
              }
            >
              {expanded
                ? <ChevronDown size={14} aria-hidden="true" />
                : <ChevronRight size={14} aria-hidden="true" />}
            </button>
          ) : (
            <span className={styles.twistySpacer} aria-hidden="true" />
          )}

          <button
            type="button"
            className={styles.label}
            onClick={() => select(isSelected ? null : node.guid)}
            aria-pressed={isSelected}
            /* An inactive geo is a location LABEL, not a joinable community. It
               scopes the catalog, but the row states plainly that there is
               nothing here to join — disable-and-explain, not a dead click. */
            title={
              node.inactive
                ? t('catalog.tree.inactiveHint', { name: node.name })
                : node.name
            }
          >
            <span className={styles.name}>{node.name}</span>
            {node.inactive && (
              <span className={styles.inactiveTag}>{t('catalog.tree.inactive')}</span>
            )}
            <span className={styles.count}>{node.subtreeCount}</span>
          </button>
        </div>

        {hasChildren && expanded && (
          <ul className={styles.children}>
            {node.children.map((c) => renderNode(c, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <Rail
      ariaLabel={t('catalog.tree.ariaLabel')}
      widthVariant="switcher"
      headerLabel={t('catalog.tree.header')}
      collapsed={persisted.collapsed}
      onToggleCollapsed={() => persisted.toggleCollapsed()}
      headerActions={
        selected ? (
          <button type="button" className={styles.clear} onClick={() => select(null)}>
            {t('catalog.tree.clear')}
          </button>
        ) : undefined
      }
    >
      {collapsed ? (
        // Collapsed strip — a single rotated "Geographies" label reading
        // bottom-to-top, clickable to expand. Mirrors ResourceRegistryRail
        // (and Discover's ActivityDrawer before it). Deliberately NOT a
        // truncated tree or a column of icons: there is no honest 56px
        // representation of a hierarchy, and a messy one is worse than none
        // (Skip, 2026-07-13).
        <button
          type="button"
          className={styles.collapsedStrip}
          onClick={() => persisted.toggleCollapsed()}
          title={t('catalog.tree.header')}
          aria-label={t('catalog.tree.header')}
        >
          <span className={styles.collapsedLabel}>{t('catalog.tree.header')}</span>
        </button>
      ) : roots.length === 0 ? (
        <div className={styles.empty}>{t('catalog.tree.empty')}</div>
      ) : (
        <ul className={styles.tree}>{roots.map((r) => renderNode(r, 0))}</ul>
      )}
    </Rail>
  );
}
